import type { FunctionComponent } from "react";

import {
  ButtonProps,
  Collapse,
  CollapseProps,
  Divider,
  Skeleton,
  SkeletonProps,
  Typography,
} from "@mui/material";
import styled from "@mui/material/styles/styled";

import Button from "@rui/Button";
import Stack, { Row, StackProps } from "@rui/Stack";

// TODO (ENG-8648) - mobile state
export const LeftPanel = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.shades.primary[700],
  color: "#FFF",
  flex: 1,
  flexBasis: "50%",
}));

export const RightPanel = styled(Stack)({
  backgroundColor: "#FFF",
  flexBasis: "50%",
  padding: "40px 80px",
});

export const PanelContentOuterContainer = styled(Stack)({
  flex: 1,
  width: "100%",
  alignItems: "center",
});

export const PanelContentInnerContainer = styled<FunctionComponent<StackProps>>(
  (props) => <Stack variant="apart" {...props} />
)({
  flex: 1,
  width: "100%",
  maxWidth: "440px",
});

export const PanelSection = styled<FunctionComponent<StackProps>>((props) => (
  <Stack variant="left" spacing={1.5} {...props} />
))({
  width: "100%",
  maxWidth: "400px",
});

export const PanelMainContent = styled<FunctionComponent<StackProps>>(
  (props) => <Stack variant="center" flex={1} spacing={4} {...props} />
)({ width: "100%" });

export const CheckoutButton = styled(Button)({
  width: "100%",
});

type PurchaseOptionProps = StackProps & {
  disabled?: boolean;
  selected: boolean;
};

export const PurchaseOption = styled<FunctionComponent<PurchaseOptionProps>>(
  ({ selected: _selected, disabled: _disabled, ...props }) => (
    <Stack {...props} />
  )
)(({ disabled = false, selected, theme }) => ({
  width: "100%",
  border: `2px solid ${
    selected && !disabled
      ? theme.palette.shades.primary[500]
      : theme.palette.shades.neutral[100]
  }`,
  borderRadius: "8px",
  padding: theme.spacing(1.5),
  paddingTop: theme.spacing(0.5),
  backgroundColor: disabled ? theme.palette.shades.neutral[50] : "#FFF",
  "& .MuiFormControlLabel-label": {
    fontWeight: 500,
    color:
      selected && !disabled
        ? theme.palette.shades.primary[500]
        : theme.palette.shades.neutral[500],
  },
  "& .MuiSvgIcon-root": {
    color:
      selected && !disabled ? undefined : theme.palette.shades.neutral[300],
  },
  "&:hover": {
    cursor: disabled ? "default" : "pointer",
  },
}));

export const DetailsHeading = styled(Typography)(({ theme }) => ({
  color: theme.palette.shades.neutral[600],
}));

export const PurchaseHeading = styled(Typography)(({ theme }) => ({
  fontSize: "1.125rem",
  color: theme.palette.shades.neutral[600],
  fontWeight: 500,
}));

export const BreakdownHighlight = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.transparent.white[12],
  width: "100%",
  padding: theme.spacing(1.5, 2),
  borderRadius: "4px",
  boxShadow: theme.shadows[4],
  border: `1px solid ${theme.palette.transparent.white[12]}`,
}));

export const CompletedPaymentInfo = styled<FunctionComponent<StackProps>>(
  (props) => <Row variant="apart" {...props} />
)(({ theme }) => ({
  backgroundColor: theme.palette.shades.ghost[300],
  color: theme.palette.shades.neutral[800],
  padding: theme.spacing(1.5),
  borderRadius: "8px",
  minHeight: "58px",
}));

export const BillingDetailsLoading = styled<FunctionComponent<SkeletonProps>>(
  (props) => <Skeleton variant="rectangular" {...props} />
)({
  height: "58px",
  width: "100%",
  borderRadius: "8px",
});

export const StripeElementContainer = styled<FunctionComponent<CollapseProps>>(
  (props) => <Collapse {...props} />
)(({ theme }) => ({
  border: "1px solid",
  borderColor: theme.palette.shades.neutral[100],
  borderRadius: "8px",
  padding: theme.spacing(2),
}));

export const EditFormButton = styled<FunctionComponent<ButtonProps>>(
  (props) => <Button variant="outlined" {...props} />
)(({ theme }) => ({
  border: "1px solid",
  borderColor: theme.palette.shades.neutral[100],
  backgroundColor: "#FFF",
  borderRadius: "4px",

  "&:hover": {
    backgroundColor: "#FFF",
  },
}));

export const LineItemDivider = styled(Divider)(({ theme }) => ({
  backgroundColor: theme.palette.transparent.white[20],
  width: "100%",
}));
